import { Box, Tabs, Tab } from '@hiiretail/react-foundation-ui';
import { ReceiptFormData } from 'common/receipt.types';
import React, { FC, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

interface ReceiptTabsProps {
  children: ReactNode[];
}
interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      style={{ flex: 1 }}
      {...other}
    >
      <Box sx={{ height: '100%', visibility: value === index ? 'visible' : 'hidden' }}>
        {children}
      </Box>
    </div>
  );
};

const styles = {
  tabs: {
    borderRadius: '4px',
    '& .MuiButtonBase-root': {
      fontSize: 18,
      textTransform: 'none',
    },
  },
  tab: {
    '&:hover': {
      backgroundColor: 'rgba(153, 100, 227, 0.04)',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(153, 100, 227, 0.08)',
    },
  },
};

const renderWithErrorStyle = (error: any) => ({
  '&.MuiTab-root': {
    backgroundColor: error ? 'rgba(255,0,0,0.05)' : 'none',
    color: error ? 'crimson' : 'rgba(0, 0, 0, 0.6)',
  },
});

export const ReceiptTabs: FC<ReceiptTabsProps> = ({ children }) => {
  const [value, setValue] = React.useState(0);
  const intl = useIntl();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const {
    formState: { errors },
  } = useFormContext<ReceiptFormData>();

  const renderErrorIndicator = {
    '& .MuiTabs-indicator': {
      backgroundColor:
        ((errors?.referencedFiscalNumber || errors?.invoiceDate) && value === 0) ||
        ((errors?.grossTotal || errors?.VATSummary) && value === 1) ||
        (errors?.customer?.country && value === 2)
          ? 'crimson'
          : 'none',
    },
  };

  return (
    <>
      <Tabs
        value={value}
        onChange={handleChange}
        aria-label="tabs"
        sx={[styles.tabs, renderErrorIndicator]}
      >
        <Tab
          value={0}
          label={intl.formatMessage({ id: 'receipt.receiptItems' })}
          sx={[
            styles.tab,
            renderWithErrorStyle(errors?.referencedFiscalNumber || errors?.invoiceDate),
          ]}
        />
        <Tab
          value={1}
          label={intl.formatMessage({ id: 'receipt.VATSummary' })}
          sx={[styles.tab, renderWithErrorStyle(errors?.grossTotal || errors?.VATSummary)]}
        />
        <Tab
          value={2}
          label={intl.formatMessage({ id: 'receipt.customer.info' })}
          sx={[styles.tab, renderWithErrorStyle(errors?.customer?.country)]}
        />
      </Tabs>
      {React.Children.map(children, (child, index) => (
        <TabPanel index={index} key={index} value={value}>
          {child}
        </TabPanel>
      ))}
    </>
  );
};
